import { gql } from '@apollo/client';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { InventoryModel, SINGLE_INVENTORY_PATH } from 'client/data/models/inventory';
import { getVinPath } from 'site-modules/shared/utils/inventory/vdp-utils';
import { CONTENT_TYPES } from 'site-modules/shared/constants/incentives';
import { WARRANTY_TYPE } from 'site-modules/shared/constants/inventory/warranty';
import { getPageTransactionType } from 'site-modules/shared/components/incentives/incentives-wizard/utils/get-page-transaction-type';

const EvIncentivesData = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string,
    programType: PropTypes.string,
    rebateAmount: PropTypes.number,
    restrictions: PropTypes.string,
    subtype: PropTypes.string,
    transactionTypes: PropTypes.arrayOf(PropTypes.string),
  })
);

const InventoryEvInsightsData = PropTypes.shape({
  style: PropTypes.shape({
    epaSpecifications: PropTypes.shape({
      electricityRange: PropTypes.number,
      batteryChargeTime: PropTypes.number,
    }),
    engine: PropTypes.shape({
      electricMotor: PropTypes.shape({
        fastChargePortType: PropTypes.string,
      }),
    }),
    batteryWarranty: PropTypes.shape({
      name: PropTypes.string,
      maxYears: PropTypes.string,
      comments: PropTypes.string,
      warrantyType: PropTypes.string,
      maxMileage: PropTypes.string,
      maxMileageType: PropTypes.string,
      maxYearsType: PropTypes.string,
    }),
    hybridComponentsWarranty: PropTypes.shape({
      maxYears: PropTypes.string,
      maxYearsType: PropTypes.oneOf(['LIMITED', 'UNLIMITED']),
      maxMileage: PropTypes.string,
      maxMileageType: PropTypes.oneOf(['LIMITED', 'UNLIMITED']),
    }),
  }),
  incentives: EvIncentivesData,
});

export const InventoryEvInsightsEntities = {
  InventoryEvInsightsData,
  EvIncentivesData,
};

const getEvInsightsAllProgramIncentivesDataPath = (vin, paymentType) =>
  vin && `vin["${vin}"].evInsightsAllProgramIncentives["${paymentType}"]`;

export const InventoryEvInsightsPaths = {
  getEvInsightsAllProgramIncentivesDataPath,
};

function buildStyleInfo(inventoryIncentives) {
  const style = get(inventoryIncentives, `${SINGLE_INVENTORY_PATH}.vinStyle.style`, {});
  const warranty = get(style, 'warranty', []) || [];
  const batteryWarranty = warranty.find(({ warrantyType }) => warrantyType === WARRANTY_TYPE.EV_BATTERY);
  const hybridComponentsWarranty = warranty.find(({ warrantyType }) => warrantyType === WARRANTY_TYPE.HYBRID_COMPONENT);

  return {
    style: {
      ...style,
      warranty: undefined,
      batteryWarranty,
      hybridComponentsWarranty,
    },
  };
}

export const InventoryEvInsightsModel = createModelSegment('inventoryEvInsights', [
  {
    /**
     * @see getEvInsightsAllProgramIncentivesDataPath
     */
    path: 'vin["{vin}"].evInsightsAllProgramIncentives["{paymentType}"]',
    async resolve({ vin, paymentType }, context) {
      const vinData = await context.resolveValue(getVinPath(vin), InventoryModel);

      const dealerZipCode = get(vinData, 'dealerInfo.address.zip');
      const engineType = get(vinData, 'vehicleInfo.partsInfo.engineType');

      const vinIncentiveFilters = {
        zip: dealerZipCode,
        engineTypes: engineType ? [engineType] : ['electric', 'plug-in hybrid'],
        types: {
          in: [
            CONTENT_TYPES.OTHER_OFFERS,
            CONTENT_TYPES.CUSTOMER_CASH,
            CONTENT_TYPES.CUSTOMER_CASH_AND_APR,
            CONTENT_TYPES.TEXT_ONLY,
            CONTENT_TYPES.NON_CASH_TO_CUSTOMER,
          ],
        },
        transactionTypes: {
          in: [getPageTransactionType(vinData.type, paymentType)],
        },
      };

      const data = await withMetrics(EdmundsGraphQLFederation, context).query(
        gql`
          query (
            $filters: InventoryFilters!
            $vinIncentiveFilters: VinIncentiveFilters!
            $eligibilityFactors: [EligibilityFactorFilter!]
            $page: PageRequest!
            $sortBy: IncentiveAggregationByGrantorAndSubtypeSort!
          ) {
            inventories(filters: $filters) {
              inventories {
                vinStyle {
                  style {
                    epaSpecifications {
                      electricityRange
                      batteryChargeTime
                    }
                    engine {
                      electricMotor {
                        fastChargePortType
                      }
                    }
                    warranty {
                      name
                      maxYears
                      comments
                      warrantyType
                      maxMileage
                      maxMileageType
                      maxYearsType
                    }
                  }
                }

                incentiveList(
                  vinIncentiveFilters: $vinIncentiveFilters
                  page: $page
                  eligibilityFactors: $eligibilityFactors
                ) {
                  incentiveAggregations {
                    byGrantorAndSubtype(page: $page, sortBy: $sortBy) {
                      topIncentiveByRebate {
                        programUrl
                        programType
                        subtype
                        subtypeId
                        rebateAmount
                        endDate
                        restrictions
                        id
                        type
                        transactionTypes
                        grantor
                        updateDate
                        compatibleIncentiveIds
                        primary
                        rebateOverridesByEligibility {
                          rebateAmount
                          eligibilityFactor {
                            qualifier
                            id
                            taxFilingStatus
                            maxIncome
                            householdSize
                            assistanceParticipant
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        {
          filters: { vins: [vin] },
          vinIncentiveFilters,
          eligibilityFactors: [],
          page: { pageNum: 1, pageSize: 16 },
          sortBy: { topIncentiveByRebateAmount: 'DESC' },
        }
      );

      return {
        ...buildStyleInfo(data),
        incentives: get(
          data,
          `${SINGLE_INVENTORY_PATH}.incentiveList.incentiveAggregations.byGrantorAndSubtype`,
          []
        ).map(incentive => get(incentive, 'topIncentiveByRebate')),
      };
    },
  },
]);
